import React, {useCallback, useEffect, useMemo, useState} from 'react';
import "./BoothMenuRecipeHistoryView.css";
import BoothMenuDiffAxiosService from "../../../common/service/BoothMenuDiffAxiosService";
import {useRelativeTime} from "../../../../../../customHooks/useRelativeTime";
import JsonView from "react18-json-view";
import {Spinner} from "react-bootstrap";
import {useTranslation} from "react-i18next";

const BoothMenuRecipeHistoryView = ({ boothId, menuId, detailId }) => {

    const {t} = useTranslation();

    const [histories, setHistories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchHistories = useCallback(async () => {
        const onSuccess = async (data) => {
            setHistories(data?.histories ?? []);
            setError(false);
            setLoading(false);
        };
        const onError = (err) => {
            setHistories([]);
            setError(true);
            setLoading(false);
        }
        setLoading(true);
        await BoothMenuDiffAxiosService.fetchBoothMenuDiffHistory(boothId, menuId, detailId, onSuccess, onError);
    }, [boothId, detailId, menuId]);

    useEffect(() => {
        if ((boothId != null) && (menuId != null) && (detailId != null)) {
            fetchHistories();
        }
    }, [boothId, menuId, detailId, fetchHistories]);

    if (loading) {
        return <div className={"booth-menu-recipe-history-view-container"}>
            <Spinner />
        </div>;
    }
    if (error) {
        return <div className={"booth-menu-recipe-history-view-container"}>{t("boothRecipeHistory.messages.error")}</div>;
    }

    return (
        <div className={"booth-menu-recipe-history-view-container"}>
            {
                (histories && histories.length > 0) ?
                    histories.map((history, index) => (
                        <HistoryItem
                            key={history.historyId + index}
                            history={history}
                        />
                    ))
                    :
                    <div>{t("boothRecipeHistory.messages.noHistory")}</div>
            }
        </div>
    );
};

const HistoryItem = ({ history }) => {

    const {t} = useTranslation();

    const relativeTime = useRelativeTime(history?.editedAt);
    const [isRecipeOpen, setIsRecipeOpen] = useState(false);

    const isEmptyRecipe = history.recipeStr === "";

    const recipeJson = useMemo(() => {
        if (history.recipeStr && !isEmptyRecipe) {
            try {
                return JSON.parse(history.recipeStr);
            } catch (e) {
                return { error: "Invalid JSON format", original: history.recipeStr };
            }
        }
        return null;
    }, [history.recipeStr, isEmptyRecipe]);

    const getSourceTypeClass = (sourceType) => {
        return `history-source-badge ${sourceType?.toLowerCase()}`;
    };

    return (
        <div className="history-item-card">
            <div className="history-item-header">
                <div className="history-item-info">
                    <span className="history-editor-id">{history.editorId}</span>
                    <span className={getSourceTypeClass(history.sourceType)}>
                        {history.sourceType}
                    </span>
                </div>
                <span className="history-edited-at">{relativeTime}</span>
            </div>

            <div className="history-item-body">
                {isEmptyRecipe ? (
                    <span className="history-recipe-removed-message">
                        {t("boothRecipeHistory.messages.removeCustomRecipe")}
                    </span>
                ) : (
                    <>
                        <button
                            className="history-toggle-btn"
                            onClick={() => setIsRecipeOpen(prev => !prev)}
                        >
                            {isRecipeOpen ? t("boothRecipeHistory.detailHide") : t("boothRecipeHistory.detailView")}
                        </button>

                        {isRecipeOpen && (
                            <div className="history-recipe-view">
                                <JsonView
                                    src={recipeJson}
                                    theme={"winter-is-coming"}
                                    enableClipboard={false}
                                    collapseStringsAfterLength={40}
                                    collapseStringMode={"directly"}
                                    collapsed={false}
                                    editable={false}
                                    displaySize={false}
                                    name={null}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default BoothMenuRecipeHistoryView;
